import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@material-ui/core";
import dayjs from "dayjs";
import useProcedureManagement from "../hooks/useProcedureManagement";
import Title from "../components/common/title";
import ContractDetail from "./ContractDetail";
import ContractAdd from "./ContractAdd";
import CustomTable from "../components/common/CustomTable";
import ConfirmationDialog from "../components/common/ConfirmationDialog";
import ContractEdit from "./ContractEdit";
import CustomLinearProgress from "../components/common/CustomLinearProgress";
import useAccount from "../hooks/useAccount";

// const activityTypes = {
//   TRA_GOP: "Trả góp",
//   TIN_CHAP: "Tín chấp",
//   THU_CHI: "Thu chi"
// };
const Report = () => {
  const {
    loading,
    handleClose,
    open,
    handleCloseCat,
    openCat,
    dataCat,
    openEdit,
    dataEdit,
    handleCloseEdit,
    openModalDelete,
    deleteId,
    remove,
    deleteModalContent,
    handleCloseModalDelete,
    tab
  } = useProcedureManagement();
  const { getActivities, activities, activitiesMetaData } = useAccount();

  // State for date filters with default value as today
  const [dateFrom, setDateFrom] = useState(dayjs().format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(dayjs().format("YYYY-MM-DD"));

  // Fetch activities with date filter when component mounts or when search is triggered
  const fetchActivities = () => {
    getActivities({
      dateFrom,
      dateTo
    });
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchActivities();
  }, []);

  // Handle search button click
  const handleSearch = () => {
    fetchActivities();
  };

  // Format number with commas and sign
  const formatNumber = (value, showSign = false) => {
    if (value === null || value === undefined) return "-";
    if (value === 0) return "0";
    const formatted = Math.abs(value).toLocaleString("en-US");
    if (showSign) {
      return value > 0 ? `+${formatted}` : `-${formatted}`;
    }
    return formatted;
  };

  // Calculate total from income and outcome
  const calculateTotal = (income, outcome) => (income || 0) - (outcome || 0);

  // Get summary data from metaData
  const summary = activitiesMetaData?.summary || {};
  const startOfDayCash = activitiesMetaData?.startOfDayCash || 0;
  const remainingCash = activitiesMetaData?.remainingCash || 0;

  const options = {
    filterType: "checkbox",
    selectableRows: false,
    downloadOptions: {
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true
      }
    }
  };

  const handleExportExcel = () => {
    const icon = document.querySelector("[data-testid='CloudDownloadIcon']");
    const parentIcon = icon.parentNode;
    if (parentIcon) {
      parentIcon.click();
    }
  };

  const columns = [
    {
      name: "contractNumber",
      label: "#",
      options: {
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return <Box>{rowIndex + 1}</Box>;
        }
      }
    },
    {
      name: "type",
      label: "Loại hình",
      options: {
        customBodyRender: value => <Box>{value}</Box>
      }
    },
    {
      name: "contractNumber",
      label: "Mã HĐ",
      options: {
        customBodyRender: value => <Box>{value}</Box>
      }
    },
    {
      name: "userName",
      label: "Người GD",
      options: {
        customBodyRender: value => value
      }
    },
    {
      name: "customerName",
      label: "Khách hàng",
      options: {
        customBodyRender: value => value
      }
    },
    {
      name: "createdAt",
      label: "Ngày GD",
      options: {
        customBodyRender: value => dayjs(value).format("DD/MM/YYYY")
      }
    },
    {
      name: "description",
      label: "Diễn giải",
      options: {
        customBodyRender: value => value
      }
    },
    {
      name: "income",
      label: "Đã thu",
      options: {
        customBodyRender: value => `${value?.toLocaleString("en-US") || 0} VNĐ`
      }
    },
    {
      name: "outcome",
      label: "Đã chi",
      options: {
        customBodyRender: value => `${value?.toLocaleString("en-US") || 0} VNĐ`
      }
    },
    {
      name: "notes",
      label: "Ghi chú",
      options: {
        customBodyRender: value => value
      }
    }
  ];

  return (
    <>
      <Title id="Báo cáo lịch sử ghi nhận" />
      {openCat && (
        <ContractDetail
          open={openCat}
          data={dataCat}
          handleClose={handleCloseCat}
          tab={tab}
        />
      )}
      {openEdit && (
        <ContractEdit
          open={openEdit}
          handleClose={handleCloseEdit}
          data={dataEdit}
        />
      )}
      <ContractAdd open={open} handleClose={handleClose} />
      <ConfirmationDialog
        open={openModalDelete}
        handleClose={handleCloseModalDelete}
        onSubmit={() => remove(deleteId)}
        title="Xóa hợp đồng"
        confirmTitle="Bạn có chắc chắn xóa hợp đồng này không?"
        content={deleteModalContent}
      />
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          py: 3
        }}
      >
        <Container maxWidth={false}>
          {/* Date filter section */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              mb: 3,
              flexWrap: "wrap"
            }}
          >
            <TextField
              label="Từ ngày"
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              InputLabelProps={{
                shrink: true
              }}
              size="small"
            />
            <TextField
              label="Đến ngày"
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              InputLabelProps={{
                shrink: true
              }}
              size="small"
            />
            <Button
              color="primary"
              variant="contained"
              className="customButton"
              onClick={handleSearch}
            >
              Tìm kiếm
            </Button>
            <Button
              color="secondary"
              size="small"
              className="customButton"
              variant="contained"
              onClick={handleExportExcel}
            >
              Xuất excel
            </Button>
          </Box>

          {/* Summary table */}
          <Box sx={{ mb: 3 }}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">
                        <strong>
                          Bảng tổng kết giao dịch{" "}
                          <span style={{ fontWeight: "normal" }}>
                            Từ ngày {dayjs(dateFrom).format("DD/MM/YYYY")} -{" "}
                            {dayjs(dateTo).format("DD/MM/YYYY")}
                          </span>
                        </strong>
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        <strong>Thu</strong>
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        <strong>Chi</strong>
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        <strong>Tổng cộng</strong>
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Tiền đầu ngày */}
                  <TableRow>
                    <TableCell>Tiền đầu ngày</TableCell>
                    <TableCell align="right">-</TableCell>
                    <TableCell align="right">-</TableCell>
                    <TableCell
                      align="right"
                      style={{ color: "#1976d2", fontWeight: "bold" }}
                    >
                      {formatNumber(startOfDayCash)}
                    </TableCell>
                  </TableRow>
                  {/* Cầm Đồ - mapped to tangVon based on API structure */}
                  <TableRow>
                    <TableCell>Cầm Đồ</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.tangVon?.income || 0) > 0
                            ? "#1976d2"
                            : "inherit"
                      }}
                    >
                      {formatNumber(summary.tangVon?.income || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.tangVon?.outcome || 0) > 0
                            ? "#f44336"
                            : "inherit"
                      }}
                    >
                      {formatNumber(summary.tangVon?.outcome || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          calculateTotal(
                            summary.tangVon?.income,
                            summary.tangVon?.outcome
                          ) >= 0
                            ? "#1976d2"
                            : "#f44336",
                        fontWeight: "bold"
                      }}
                    >
                      {formatNumber(
                        calculateTotal(
                          summary.tangVon?.income,
                          summary.tangVon?.outcome
                        ),
                        true
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Tín Chấp */}
                  <TableRow>
                    <TableCell>Tín Chấp</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.tinChap?.income || 0) > 0
                            ? "#1976d2"
                            : "inherit"
                      }}
                    >
                      {formatNumber(summary.tinChap?.income || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.tinChap?.outcome || 0) > 0
                            ? "#f44336"
                            : "inherit"
                      }}
                    >
                      {formatNumber(summary.tinChap?.outcome || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          calculateTotal(
                            summary.tinChap?.income,
                            summary.tinChap?.outcome
                          ) >= 0
                            ? "#1976d2"
                            : "#f44336",
                        fontWeight: "bold"
                      }}
                    >
                      {formatNumber(
                        calculateTotal(
                          summary.tinChap?.income,
                          summary.tinChap?.outcome
                        ),
                        true
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Trả Góp */}
                  <TableRow>
                    <TableCell>Trả Góp</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.traGop?.income || 0) > 0
                            ? "#1976d2"
                            : "inherit"
                      }}
                    >
                      {formatNumber(summary.traGop?.income || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.traGop?.outcome || 0) > 0
                            ? "#f44336"
                            : "inherit"
                      }}
                    >
                      {(summary.traGop?.outcome || 0) > 0 ? "-" : ""}
                      {formatNumber(summary.traGop?.outcome || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          calculateTotal(
                            summary.traGop?.income,
                            summary.traGop?.outcome
                          ) >= 0
                            ? "#1976d2"
                            : "#f44336",
                        fontWeight: "bold"
                      }}
                    >
                      {formatNumber(
                        calculateTotal(
                          summary.traGop?.income,
                          summary.traGop?.outcome
                        ),
                        true
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Thu Hoạt Động - using thuChi income */}
                  <TableRow>
                    <TableCell>Thu Hoạt Động</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.thuChi?.income || 0) > 0
                            ? "#1976d2"
                            : "inherit"
                      }}
                    >
                      {formatNumber(summary.thuChi?.income || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.thuChi?.outcome || 0) > 0
                            ? "#f44336"
                            : "inherit"
                      }}
                    >
                      {formatNumber(0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.thuChi?.income || 0) >= 0
                            ? "#1976d2"
                            : "#f44336",
                        fontWeight: "bold"
                      }}
                    >
                      {formatNumber(summary.thuChi?.income || 0)}
                    </TableCell>
                  </TableRow>
                  {/* Chi Hoạt Động - using thuChi outcome */}
                  <TableRow>
                    <TableCell>Chi Hoạt Động</TableCell>
                    <TableCell align="right" style={{ color: "#1976d2" }}>
                      {formatNumber(0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.thuChi?.outcome || 0) > 0
                            ? "#f44336"
                            : "inherit"
                      }}
                    >
                      {formatNumber(summary.thuChi?.outcome || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.thuChi?.outcome || 0) > 0
                            ? "#f44336"
                            : "#1976d2",
                        fontWeight: "bold"
                      }}
                    >
                      {(summary.thuChi?.outcome || 0) > 0 ? "-" : ""}
                      {formatNumber(summary.thuChi?.outcome || 0)}
                    </TableCell>
                  </TableRow>
                  {/* Nguồn Vốn */}
                  <TableRow>
                    <TableCell>Nguồn Vốn</TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.tangVon?.income || 0) > 0
                            ? "#1976d2"
                            : "inherit"
                      }}
                    >
                      {formatNumber(summary.tangVon?.income || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          (summary.tangVon?.outcome || 0) > 0
                            ? "#f44336"
                            : "inherit"
                      }}
                    >
                      {formatNumber(summary.tangVon?.outcome || 0)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{
                        color:
                          calculateTotal(
                            summary.tangVon?.income,
                            summary.tangVon?.outcome
                          ) >= 0
                            ? "#1976d2"
                            : "#f44336",
                        fontWeight: "bold"
                      }}
                    >
                      {formatNumber(
                        calculateTotal(
                          summary.tangVon?.income,
                          summary.tangVon?.outcome
                        ),
                        true
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Tiền mặt còn lại */}
                  <TableRow>
                    <TableCell>
                      <strong>Tiền mặt còn lại</strong>
                    </TableCell>
                    <TableCell align="right">-</TableCell>
                    <TableCell align="right">-</TableCell>
                    <TableCell
                      align="right"
                      style={{ color: "#1976d2", fontWeight: "bold" }}
                    >
                      {formatNumber(remainingCash)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ pt: 3 }}>
            {loading ? (
              <CustomLinearProgress />
            ) : (
              <CustomTable
                title={
                  <Typography variant="h2">Báo cáo lịch sử ghi nhận</Typography>
                }
                data={activities}
                options={options}
                columns={columns}
                id="bao-cao-lich-su-ghi-nhan"
              />
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Report;
